import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { Box, Link, Stack, type Theme } from "@mui/material";

import { Text } from "../../components/Text";
import { getBorderBase, getMutedText, getStrongText } from "../themeHelpers";

export interface SpecialistCardData {
  name: string;
  phone?: string;
  email?: string;
  workTimings: string;
}

function getSectionHeadingSx(theme: Theme, isMobile: boolean) {
  return {
    fontFamily: theme.typography.body1.fontFamily,
    fontSize: isMobile ? theme.typography.h6.fontSize : theme.typography.h5.fontSize,
    lineHeight: 1.2,
    color: getStrongText(theme),
  };
}

interface SpecialistCardProps {
  title: string;
  specialist: SpecialistCardData;
}

function SpecialistCard({ title, specialist }: SpecialistCardProps) {
  const { name, phone, email, workTimings } = specialist;

  return (
    <Box
      sx={(theme) => ({
        flex: 1,
        borderRadius: theme.borderRadius?.xLarge,
        border: `1px solid ${getBorderBase(theme)}`,
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(4),
        boxShadow: theme.shadow?.card,
      })}
    >
      <Stack spacing={2}>
        <Box>
          <Text
            bold
            variant="body1"
            sx={(theme) => ({ color: getStrongText(theme), lineHeight: 1.2 })}
          >
            {title}
          </Text>
          {workTimings.length > 0 && (
            <Text variant="caption" sx={(theme) => ({ color: getMutedText(theme) })}>
              {workTimings}
            </Text>
          )}
        </Box>
        <Text variant="body2" sx={(theme) => ({ color: getStrongText(theme) })}>
          {name}
        </Text>
        <Stack spacing={1}>
          {phone && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <PhoneOutlinedIcon
                fontSize="small"
                sx={(theme) => ({ color: getMutedText(theme) })}
              />
              <Link href={`tel:${phone}`} underline="hover" variant="body2">
                {phone}
              </Link>
            </Stack>
          )}
          {email && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <EmailOutlinedIcon
                fontSize="small"
                sx={(theme) => ({ color: getMutedText(theme) })}
              />
              <Link href={`mailto:${email}`} underline="hover" variant="body2">
                {email}
              </Link>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

interface HelpCenterSpecialistSectionProps {
  isMobile: boolean;
  amSpecialist?: SpecialistCardData;
  billingSpecialist?: SpecialistCardData;
}

export function HelpCenterSpecialistSection({
  isMobile,
  amSpecialist,
  billingSpecialist,
}: HelpCenterSpecialistSectionProps) {
  if (!amSpecialist && !billingSpecialist) {
    return null;
  }

  return (
    <Stack spacing={2}>
      <Text bold variant="body1" sx={(theme) => getSectionHeadingSx(theme, isMobile)}>
        Dedicated contacts
      </Text>
      <Text variant="body2" sx={(theme) => ({ color: getMutedText(theme) })}>
        Your dedicated Clipboard Health team members.
      </Text>
      <Box
        sx={(theme) => ({
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
          gap: theme.spacing(2),
        })}
      >
        {amSpecialist && <SpecialistCard title="Account Manager" specialist={amSpecialist} />}
        {billingSpecialist && <SpecialistCard title="Billing Rep" specialist={billingSpecialist} />}
      </Box>
    </Stack>
  );
}
